import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  GridLegacy as Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { resetPassword, signIn, signInWithRedirect } from "aws-amplify/auth";
import { usePageTitle } from "hooks/usePageTitle";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { clearAuthFlowCookie, setAuthFlowCookie } from "utils/authFlowCookie";
import EncampWideLogo from "../assets/EncampWideLogo.svg";
import {
  ENCAMP_SSO_PROVIDER,
  isEncampSSOEmail,
  LAST_SSO_LOGIN_KEY,
  LastSsoLogin,
} from "../providers/user";
import { useAlerts } from "./Alerts/AlertProvider";
import { ConfirmDialog } from "./ConfirmDialog";
import { FormTextField } from "./Forms/FormTextField";
import { PasswordAction, PasswordDialog } from "./PasswordDialog";
import { SpinnerOverlay } from "./SpinnerOverlay";

type SignInFormProps = PropsWithChildren<any>;

const USER_DISABLED_MESSAGE =
  "Invalid SAML response received: User is disabled. ";

export function SignInForm({ children }: SignInFormProps) {
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showConfirmResetPasswordDialog, setShowConfirmResetPasswordDialog] =
    useState(false);
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    reset,
    formState: { isSubmitting },
  } = useForm<{
    email: string;
    password: string;
  }>({
    defaultValues: { email: "", password: "" },
  });
  const { authStatus } = useAuthenticator();
  const alerts = useAlerts();
  const theme = useTheme();
  const [passwordAction, setPasswordAction] = useState<PasswordAction | null>(
    null
  );
  const [passwordResetEmail, setPasswordResetEmail] = useState<string | null>(
    null
  );

  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [passwordResetCode, setPasswordResetCode] = useState<string | null>(null);
  const resetDeepLinkHandledRef = useRef(false);

  const searchParams = useMemo(
    () => new URLSearchParams(routerLocation.search),
    [routerLocation.search]
  );
  const hasDomainQuery = searchParams.has("domain");

  const { email, password } = watch();

  const emailInvalid = useMemo(() => {
    if (!email?.length) return true;
    if (!/^([^\s@.]+\.)*[^\s@.]+@[^\s@.]+(\.[^\s@.]+)+$/.test(email))
      return true;

    return false;
  }, [email]);

  // Helper to look up tenant SSO provider by email domain
  const fetchTenantSsoProvider = useCallback(
    async (domain?: string): Promise<string | null> => {
      if (!domain) {
        return null;
      }
      const apiUrl = `${
        import.meta.env.MODE === "development"
          ? "http://localhost:4000"
          : `${import.meta.env.VITE_API_URL}`
      }`;
      const response = await fetch(
        `${apiUrl}/tenantSso?domain=${encodeURIComponent(domain)}`
      );
      if (!response.ok) {
        throw new Error("tenantSso lookup failed");
      }
      const json = await response.json();
      const provider =
        typeof json?.customProvider === "string"
          ? json.customProvider.trim()
          : "";
      return provider.length ? provider : null;
    },
    []
  );

  // NOTE: Previously there was a useEffect here that switched the auth flow cookie
  // from "idp" to "sp" on mount. This was removed because it caused a race condition:
  // The effect ran before Amplify finished checking for existing tokens, causing
  // IDP-authenticated users to appear unauthenticated in new tabs.
  // See: ENG-13412 - IDP SSO sessions not persisting across browser tabs
  // The cookie is correctly set to "sp" when triggerSsoLogin() is called (line ~245).

  useEffect(() => {
    if (routerLocation.pathname !== "/reset-password") return;
    if (resetDeepLinkHandledRef.current) return;

    resetDeepLinkHandledRef.current = true;

    const emailParam = (searchParams.get("email") ?? "").trim();
    const codeParam = (searchParams.get("code") ?? "").trim();

    if (!emailParam) {
      navigate("/", { replace: true });
      return;
    }

    localStorage.removeItem("preLoginPath");

    setPasswordResetEmail(emailParam.toLowerCase());
    setPasswordResetCode(codeParam.length ? codeParam : null);
    setShowConfirmResetPasswordDialog(false);
    setShowPasswordField(false);
    setPasswordAction(PasswordAction.Reset);

    const scrubbedParams = new URLSearchParams();
    scrubbedParams.set("email", emailParam);
    navigate(`/reset-password?${scrubbedParams.toString()}`, { replace: true });
  }, [navigate, routerLocation.pathname, searchParams]);

  // Last successful SSO account (email + provider), for prefill UX
  const [storedSsoLogin, setStoredSsoLogin] = useState<LastSsoLogin | null>(
    () => {
      try {
        const raw = localStorage.getItem(LAST_SSO_LOGIN_KEY);
        const parsed = raw ? (JSON.parse(raw) as LastSsoLogin) : null;
        if (parsed && parsed.provider && parsed.email) {
          return parsed;
        }
        return null;
      } catch {
        return null;
      }
    }
  );

  const [useStoredSsoAccount, setUseStoredSsoAccount] = useState(
    Boolean(storedSsoLogin)
  );

  /**
   * Handle Cognito SSO errors returned from the identity provider.
   *
   * After an SSO redirect, Cognito may store error information in localStorage.
   * This effect checks for and displays those errors with user-friendly messages.
   */
  useEffect(() => {
    let cognitoErrorDescription = localStorage.getItem("error_description");
    if (cognitoErrorDescription) {
      console.error({ cognitoErrorDescription });

      // Translate technical Cognito errors into user-friendly messages
      if (cognitoErrorDescription === USER_DISABLED_MESSAGE) {
        cognitoErrorDescription = "User is disabled.";
      } else if (
        cognitoErrorDescription.includes(
          "Assertion ID in the SAML assertion has already been presented"
        )
      ) {
        cognitoErrorDescription =
          "This login attempt has expired. Please try logging in again.";
        // Clear the auth flow cookie in case it was set
        clearAuthFlowCookie();
      }
      setError("email", { message: cognitoErrorDescription });
      localStorage.removeItem("error_description");
    }
  }, [alerts, searchParams, setError]);

  const [autoSsoLoading, setAutoSsoLoading] = useState(false);
  const [autoSsoErrorMessage, setAutoSsoErrorMessage] = useState<string | null>(
    null
  );

  const handlePasswordLogin = useCallback(async () => {
    try {
      const lowerCaseEmail = email.toLowerCase();
      const result = await signIn({ username: lowerCaseEmail, password });
      setAuthFlowCookie("sp"); // Set after successful password login
      // Clear any stored SSO suggestion if password login is used
      localStorage.removeItem(LAST_SSO_LOGIN_KEY);
      setPasswordResetEmail(lowerCaseEmail);
      if (
        result.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setPasswordAction(PasswordAction.NewPassword);
      } else if (result.nextStep.signInStep === "DONE") {
        reset();
      }
      setShowPasswordField(false);
    } catch (err) {
      alerts.error("Error signing in: ", err);
      if (err instanceof Error) {
        setError("password", { message: err.message });
      }
    }
  }, [email, password, alerts, setError, reset]);

  const triggerSsoLogin = useCallback(
    async (provider: string) => {
      const redirectUri = searchParams.get("redirectUri") ?? undefined;
      const customState = {
        pathname: location.pathname,
        redirectUri,
        provider,
      };
      try {
        setAuthFlowCookie("sp");
        await signInWithRedirect({
          provider: { custom: provider },
          customState: JSON.stringify(customState),
        });
      } catch (err) {
        alerts.error("Error redirecting to SSO provider: ", err);
      }
    },
    [searchParams, alerts]
  );

  const onSubmit = useCallback<(args: { email: string }) => Promise<void>>(
    async ({ email }) => {
      try {
        let customProvider: string;

        if (isEncampSSOEmail(email)) {
          customProvider = ENCAMP_SSO_PROVIDER;
        } else {
          const emailDomain = email.split("@").pop()?.toLowerCase();
          const provider = await fetchTenantSsoProvider(emailDomain);
          if (!provider) {
            setShowPasswordField(true);
            return;
          }
          customProvider = provider;
        }

        await triggerSsoLogin(customProvider);
        reset();
      } catch (err) {
        console.error(err);
        const message =
          "There was an error retrieving sign-in information for your organization. If you are unable to sign in please contact support.";
        alerts.error(message);

        setError("password", {
          message,
        });
        // Fallback to password login
        setShowPasswordField(true);
      }
    },
    [triggerSsoLogin, fetchTenantSsoProvider, alerts, setError, reset]
  );

  const goBack = useCallback(() => {
    setShowPasswordField(false);
    setValue("password", "");
  }, [setValue]);

  // Post-auth persistence is handled in providers/user.tsx

  // Validate stored SSO login against current tenant configuration
  useEffect(() => {
    let cancelled = false;
    const validate = async () => {
      if (!storedSsoLogin) return;
      try {
        const lowerEmail = storedSsoLogin.email.toLowerCase();
        if (isEncampSSOEmail(lowerEmail)) {
          if (storedSsoLogin.provider !== ENCAMP_SSO_PROVIDER) {
            const updated: LastSsoLogin = {
              email: lowerEmail,
              provider: ENCAMP_SSO_PROVIDER,
            };
            if (!cancelled) {
              setStoredSsoLogin(updated);
              localStorage.setItem(LAST_SSO_LOGIN_KEY, JSON.stringify(updated));
            }
          }
          return;
        }

        const emailDomain = lowerEmail.split("@").pop()?.toLowerCase();
        const provider = await fetchTenantSsoProvider(emailDomain);
        if (cancelled) return;
        if (!provider) {
          localStorage.removeItem(LAST_SSO_LOGIN_KEY);
          setStoredSsoLogin(null);
          setUseStoredSsoAccount(false);
          return;
        }
        if (provider !== storedSsoLogin.provider) {
          const updated: LastSsoLogin = { email: lowerEmail, provider };
          setStoredSsoLogin(updated);
          localStorage.setItem(LAST_SSO_LOGIN_KEY, JSON.stringify(updated));
        }
      } catch {
        // Swallow validation errors; do not disrupt sign-in UI
      }
    };
    validate();
    return () => {
      cancelled = true;
    };
  }, [storedSsoLogin, fetchTenantSsoProvider]);

  /**
   * Auto SSO Redirect Handler
   *
   * Automatically initiates SSO login when a `?domain=example.com` query parameter
   * is present in the URL. This enables direct deep-linking to organization-specific
   * sign-in pages.
   *
   * Flow:
   * 1. Wait for unauthenticated state (skip if configuring or authenticated)
   * 2. Extract the domain parameter from the URL
   * 3. Look up the tenant's SSO provider configuration via backend API
   * 4. Automatically redirect to the SSO provider
   * 5. Handle errors gracefully with user-friendly messages
   *
   * Domain Validation:
   * Domain normalization (trim, lowercase) is handled by the backend API.
   * The backend performs a case-insensitive database lookup and returns null
   * if no tenant is found for that domain.
   *
   * Special Cases:
   * - encamp.com always uses the Encamp Google SSO provider
   * - Unknown domains show a warning and fall back to email sign-in
   *
   * Loading State:
   * - Sets autoSsoLoading to true during the redirect process
   * - Shows a spinner overlay to prevent user interaction
   * - Clears loading state if authentication status changes (e.g., successful login)
   * - Clears loading state on error (allows fallback to email sign-in)
   */
  useEffect(() => {
    if (authStatus !== "unauthenticated") {
      if (autoSsoLoading) {
        setAutoSsoLoading(false);
      }
      return;
    }

    const domain = searchParams.get("domain");
    if (!domain) {
      return;
    }

    let cancelled = false;
    const startAutoSso = async () => {
      setAutoSsoLoading(true);
      setAutoSsoErrorMessage(null);
      let redirectStarted = false;
      try {
        localStorage.removeItem("preLoginPath");
        let provider: string | null;

        // Special case: encamp.com always uses Google SSO
        if (domain.trim().toLowerCase() === "encamp.com") {
          provider = ENCAMP_SSO_PROVIDER;
        } else {
          provider = await fetchTenantSsoProvider(domain);
        }

        if (cancelled) return;

        if (!provider) {
          setAutoSsoErrorMessage(
            "We couldn't find single sign-on for that organization. Continue with your email to sign in."
          );
          return;
        }

        redirectStarted = true;
        await triggerSsoLogin(provider);
      } catch (error) {
        if (cancelled) return;
        // Handle API errors (network issues, server errors, etc.)
        setAutoSsoErrorMessage(
          "There was a problem looking up single sign-on. Continue with your email to sign in."
        );
        redirectStarted = false;
      } finally {
        if (!cancelled && !redirectStarted) {
          setAutoSsoLoading(false);
        }
      }
    };

    void startAutoSso();

    return () => {
      cancelled = true;
    };
    // Note: autoSsoLoading is intentionally excluded from dependencies.
    // It's only managed internally by this effect and including it would cause
    // an infinite loop (effect sets loading → dependency changes → effect reruns).
    // The early return at line 375 safely reads autoSsoLoading during cleanup
    // when authStatus changes (which IS a dependency).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, fetchTenantSsoProvider, searchParams, triggerSsoLogin]);

  useEffect(() => {
    if (authStatus !== "unauthenticated") return;

    if (hasDomainQuery) {
      localStorage.removeItem("preLoginPath");
      return;
    }

    if (routerLocation.pathname === "/reset-password") {
      localStorage.removeItem("preLoginPath");
      return;
    }

    const preLoginPath = `${routerLocation.pathname}${routerLocation.search}`;
    if (preLoginPath !== "/" && localStorage.getItem("logout") !== "true") {
      localStorage.setItem(
        "preLoginPath",
        `${routerLocation.pathname}${routerLocation.search}`
      );
    }

    localStorage.removeItem("logout");
  }, [
    authStatus,
    hasDomainQuery,
    routerLocation.pathname,
    routerLocation.search,
  ]);

  const pageTitle = authStatus === "unauthenticated" ? "Sign in" : undefined;
  usePageTitle(pageTitle);

  if (authStatus === "configuring" || autoSsoLoading) {
    return <SpinnerOverlay loading />;
  }

  if (authStatus === "authenticated") {
    return children;
  }

  return (
    <>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: `url(/sign-in-background.webp)`,
            backgroundRepeat: "no-repeat",
            backgroundColor: "white",
            backgroundSize: "cover",
            backgroundPosition: "bottom",
          }}
        />
        <CssBaseline />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: "xs",
            }}
          >
            <img
              src={EncampWideLogo}
              alt="Encamp logo"
              style={{ width: "250px" }}
            />
            <Typography
              paddingTop={theme.spacing(3)}
              component="h1"
              variant="h5"
            >
              Sign in
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              display={"block"}
              sx={{ mt: 1, width: "100%", maxWidth: "500px" }}
            >
              {autoSsoErrorMessage && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {autoSsoErrorMessage}
                </Alert>
              )}
              {showPasswordField ? (
                <FormTextField
                  fullWidth
                  label="Email"
                  readOnly
                  defaultValue={email}
                  helperText=""
                  textFieldProps={{
                    margin: "normal",
                  }}
                  inputProps={{ autoComplete: "username" }}
                  show1Pass
                />
              ) : useStoredSsoAccount && storedSsoLogin ? (
                <FormTextField
                  fullWidth
                  label="Email"
                  readOnly
                  defaultValue={storedSsoLogin.email}
                  helperText=""
                  textFieldProps={{
                    margin: "normal",
                  }}
                  inputProps={{ autoComplete: "username" }}
                  show1Pass
                />
              ) : (
                <FormTextField
                  fullWidth
                  control={control}
                  label="Email"
                  name="email"
                  helperText=""
                  textFieldProps={{
                    required: true,
                    autoFocus: true,
                    margin: "normal",
                  }}
                  inputProps={{ autoComplete: "username" }}
                  show1Pass
                  type="email"
                />
              )}

              {showPasswordField && (
                <FormTextField
                  fullWidth
                  control={control}
                  label="Password"
                  name="password"
                  type="password"
                  textFieldProps={{
                    autoFocus: true,
                    required: true,
                    margin: "normal",
                  }}
                  inputProps={{ autoComplete: "current-password" }}
                  show1Pass
                />
              )}

              {!showPasswordField ? (
                useStoredSsoAccount && storedSsoLogin ? (
                  <>
                    <Button
                      fullWidth
                      sx={{ mt: 1, mb: 1 }}
                      variant="contained"
                      color="secondary"
                      onClick={() => triggerSsoLogin(storedSsoLogin.provider)}
                    >
                      Sign in with SSO
                    </Button>
                    <Box textAlign="center">
                      <Button
                        onClick={() => {
                          localStorage.removeItem(LAST_SSO_LOGIN_KEY);
                          setStoredSsoLogin(null);
                          setUseStoredSsoAccount(false);
                        }}
                      >
                        Use a different email
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Grid container>
                    <Grid item xs>
                      <Button
                        type="submit"
                        fullWidth
                        sx={{ mt: 1 }}
                        variant="contained"
                        disabled={emailInvalid}
                        loading={isSubmitting}
                      >
                        Next
                      </Button>
                    </Grid>
                  </Grid>
                )
              ) : (
                <>
                  <Button
                    type="submit"
                    fullWidth
                    sx={{ mt: 1, mb: 2 }}
                    variant="contained"
                    disabled={!email?.length || !password?.length}
                    loading={isSubmitting}
                    onClick={handleSubmit(handlePasswordLogin)}
                  >
                    Sign In
                  </Button>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Button
                      onClick={() => setShowConfirmResetPasswordDialog(true)}
                    >
                      Forgot Password?
                    </Button>
                    <Box padding={theme.spacing(1)}>
                      <Button onClick={() => goBack()}>Back</Button>
                    </Box>
                  </Stack>
                </>
              )}
              <Copyright sx={{ mt: 5 }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <PasswordDialog
        action={passwordAction}
        open={passwordAction != null}
        onClose={() => {
          setPasswordAction(null);
          setPasswordResetCode(null);
        }}
        email={passwordResetEmail ?? email}
        initialCode={passwordResetCode}
      />
      <ConfirmDialog
        open={showConfirmResetPasswordDialog}
        onConfirm={async () => {
          const lowerCaseEmail = email.toLowerCase();
          try {
            await resetPassword({ username: lowerCaseEmail });
          } catch (err) {
            alerts.error("Error generating reset code: ", err);
          }
          setPasswordResetEmail(lowerCaseEmail);
          setShowConfirmResetPasswordDialog(false);
          setShowPasswordField(false);
          setPasswordAction(PasswordAction.Reset);
        }}
        onClose={() => setShowConfirmResetPasswordDialog(false)}
        header="Are you sure you would like to reset your password?"
        info={
          'If you\'ve lost your password, click "Reset Password" to receive a one-time code to reset your password.'
        }
        confirmText="Reset Password"
      />
    </>
  );
}

function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {`© ${new Date().getFullYear()} `}
      <Link color="inherit" href="https://encamp.com/" target="_blank">
        Encamp, Inc.
      </Link>{" "}
      All Rights Reserved.
    </Typography>
  );
}
